import React from 'react';
import { Link } from 'react-router-dom';
import ActivityTypeSelect from './ActivityTypeSelect';
import TravelRoutingEditor from './TravelRoutingEditor';
import DescriptionDisplay from './DescriptionDisplay';

export default function ActivityTableRow({
  row,
  date,
  dayKey,
  tripId,
  destinationId,
  activityTypes,
  activityCategories,
  isTravelTypeId,
  isTravelCategoryId,
  editingByActivityId,
  setEditingByActivityId,
  selectedActivityIdsByKey,
  setSelectedActivityIdsByKey,
  openActivityMenuId,
  setOpenActivityMenuId,
  openNativePicker,
  refetchTypes,
  saveActivity,
  cancelEditing,
  removeActivity,
  openEditActivityModal,
}) {
  const activity = row.activity;
  const isLegRow = row.row_kind === 'leg';
  const isPrimaryForDay = !!row.primary_for_day;
  const editCtx = editingByActivityId[activity.activity_id] || null;
  const isEditing = !!editCtx && String(editCtx?.date || '') === String(row.row_date || '');
  const edit = editingByActivityId[activity.activity_id] || {};
  const effectiveTravelCategoryId = edit.category_id || String(
    (activityTypes.find(t => String(t.type_id) === String(edit.type_id || activity.type_id))?.category_id) || ''
  );
  const startDisplay = isEditing ? '' : (row.start_local || '');
  const dayOff = Number(row?.end_day_offset) || 0;
  const endDisplay = isEditing
    ? ''
    : (
      (isLegRow || row.row_kind === 'activity') && dayOff > 0
        ? (
            <span style={{ whiteSpace: 'nowrap' }}>
              {row.end_local || ''}
              <sup style={{ fontSize: '0.7em', opacity: 0.85, marginLeft: 1 }}>+{dayOff}</sup>
            </span>
          )
        : (row.end_local || '')
    );
  const selected = Array.isArray(selectedActivityIdsByKey[dayKey]) ? selectedActivityIdsByKey[dayKey] : [];
  const isSelected = selected.map(String).includes(String(activity.activity_id));

  function getTypeName(type_id) {
    const t = activityTypes.find(t => t.type_id === type_id || t.type_id === String(type_id));
    return t ? t.type_name : '';
  }
  function getCategoryName(type_id) {
    const t = activityTypes.find(t => t.type_id === type_id || t.type_id === String(type_id));
    if (!t) return '';
    const c = activityCategories.find(c => c.category_id === t.category_id || c.category_id === String(t.category_id));
    return c ? c.category_name : '';
  }

  function setEdit(updater) {
    setEditingByActivityId(prev => {
      const cur = prev[activity.activity_id] || {};
      const next = typeof updater === 'function' ? updater(cur) : updater;
      return { ...prev, [activity.activity_id]: next };
    });
  }

  return (
    <tr key={row.row_id}>
      <td className="tp-td--tight">
        {isPrimaryForDay && (
          <input
            type="checkbox"
            checked={isSelected}
            aria-label={`Select ${activity.title}`}
            onChange={(e) => {
              const checked = e.target.checked;
              setSelectedActivityIdsByKey(prev => {
                const existing = Array.isArray(prev[dayKey]) ? prev[dayKey].map(String) : [];
                const set = new Set(existing);
                if (checked) set.add(String(activity.activity_id));
                else set.delete(String(activity.activity_id));
                return { ...prev, [dayKey]: Array.from(set) };
              });
            }}
          />
        )}
      </td>
      <td>
        {isEditing ? (
          <input
            type="text"
            value={edit.title || ''}
            onChange={e => setEdit(prev => ({ ...prev, title: e.target.value }))}
          />
        ) : (
          <>
            <Link
              to={`/itinerary/${tripId}/activity/${activity.activity_id}`}
              style={{ color: 'inherit', textDecoration: 'underline' }}
            >
              {activity.title}
              {isLegRow && (
                <div style={{ fontSize: 12, color: 'var(--tp-muted)', marginTop: 2 }}>
                  Leg {Number(row.leg_index) + 1}
                </div>
              )}
            </Link>
            {Array.isArray(activity.clips) && activity.clips.length > 0 && (
              <span
                title={`${activity.clips.length} inspiration clip${activity.clips.length !== 1 ? 's' : ''} linked`}
                style={{ marginLeft: 6, fontSize: 11, color: 'var(--tp-muted)', whiteSpace: 'nowrap' }}
              >
                ▶ {activity.clips.length}
              </span>
            )}
          </>
        )}
      </td>
      <td>
        {isEditing ? (
          <input
            type="time"
            step="60"
            value={edit.start_time || ''}
            onClick={openNativePicker}
            onChange={e => setEdit(prev => ({ ...prev, start_time: e.target.value }))}
          />
        ) : startDisplay}
      </td>
      <td>
        {isEditing ? (
          <input
            type="time"
            step="60"
            value={edit.end_time || ''}
            onClick={openNativePicker}
            onChange={e => setEdit(prev => ({ ...prev, end_time: e.target.value }))}
          />
        ) : endDisplay}
      </td>
      <td>
        {isEditing ? (
          <div style={{ display: 'grid', gap: 8 }}>
            {(isTravelTypeId(edit.type_id || activity.type_id) || isTravelCategoryId(edit.category_id) || (Array.isArray(activity?.legs) && activity.legs.length)) && (
              <TravelRoutingEditor
                draft={edit}
                setDraft={setEdit}
                activityTypes={activityTypes}
                categoryId={effectiveTravelCategoryId}
                fallbackDate={edit.date}
                onTypeCreated={refetchTypes}
              />
            )}
            <textarea
              rows={3}
              placeholder="Description (Markdown)"
              value={edit.description || ''}
              onChange={e => setEdit(prev => ({ ...prev, description: e.target.value }))}
              style={{ resize: 'vertical', minHeight: 60 }}
            />
          </div>
        ) : (
          (() => {
            const locationName = String(activity?.location_address || '').trim() || String(activity?.location_city || '').trim();
            const hasDesc = String(activity?.description || '').trim();
            return (
              <div style={{ display: 'grid', gap: 6 }}>
                {hasDesc && <DescriptionDisplay text={activity.description} />}
                {locationName && (
                  <div style={{ fontSize: 12, color: 'var(--tp-muted)' }}>
                    Location: {locationName}
                  </div>
                )}
                {!hasDesc && !locationName && '—'}
              </div>
            );
          })()
        )}
      </td>
      {isEditing ? (
        <td colSpan={2}>
          <ActivityTypeSelect
            categories={activityCategories}
            types={activityTypes}
            categoryId={edit.category_id}
            typeId={edit.type_id}
            onCategoryChange={v => setEdit(prev => ({ ...prev, category_id: v, type_id: '' }))}
            onTypeChange={v => setEdit(prev => ({ ...prev, type_id: v }))}
            onTypeCreated={newType => {
              refetchTypes();
              setEdit(prev => ({ ...prev, type_id: newType.type_id }));
            }}
            placeholderCategory="Select Category"
            placeholderType="Select Type"
          />
        </td>
      ) : (
        <>
          <td>{getCategoryName(activity.type_id)}</td>
          <td>{getTypeName(activity.type_id)}</td>
        </>
      )}
      <td className="tp-td--tight">
        {isEditing ? (
          <div className="tp-row" style={{ justifyContent: 'flex-end', gap: 8 }}>
            <button onClick={() => saveActivity(activity.activity_id, destinationId)}>Save</button>
            <button className="tp-btn--ghost" onClick={() => cancelEditing(activity.activity_id)}>Cancel</button>
          </div>
        ) : (
          <div style={{ position: 'relative', display: 'flex', justifyContent: 'flex-end' }}>
            <button
              type="button"
              className="tp-btn--icon"
              aria-label="Activity actions"
              onClick={(e) => {
                e.stopPropagation();
                setOpenActivityMenuId((cur) => (String(cur) === String(activity.activity_id) ? null : activity.activity_id));
              }}
            >
              ⋮
            </button>
            {String(openActivityMenuId) === String(activity.activity_id) && (
              <div
                className="tp-card tp-card--tight tp-menu"
                style={{ position: 'absolute', right: 0, top: 'calc(100% + 6px)', zIndex: 50, minWidth: 140 }}
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  type="button"
                  className="tp-btn--ghost"
                  style={{ width: '100%', textAlign: 'left' }}
                  onClick={() => {
                    setOpenActivityMenuId(null);
                    openEditActivityModal(activity, date, destinationId);
                  }}
                >
                  Edit
                </button>
                <button
                  type="button"
                  className="tp-btn--danger"
                  style={{ width: '100%', textAlign: 'left', marginTop: 8 }}
                  onClick={() => {
                    setOpenActivityMenuId(null);
                    removeActivity(activity.activity_id);
                  }}
                >
                  Delete
                </button>
              </div>
            )}
          </div>
        )}
      </td>
    </tr>
  );
}
